import React, { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "quilljs-markdown/dist/quilljs-markdown-common-style.css";
import Dropdown from "react-bootstrap/Dropdown";
import QuillMarkdown from "quilljs-markdown";
import Skeleton from "react-loading-skeleton";
import { formats } from "./EditorToolbar.js";
import {
  CircleCrossIcon,
  ColorStarsIcon,
  ImproveIcon,
  LongerIcon,
  MoreIcon,
  ShorterIcon,
} from "../../Assets/Icons.js";

const QuillEditor = ({
  id,
  setValue,
  value,
  onFocus,
  currentToolbar,
  onTextSelection,
  setText,
  rephraseHandler,
  showAIModal,
  setShowAIModal,
  modalLoading,
  generatedText,
  idMapping,
  onReplaceClick,
  showTextModal,
  setShowTextModal,
  onRegenerateClick,
  minWordLength = 10,
  editorRef,
  readOnly = false,
}) => {
  const [selectionPosition, setSelectionPosition] = useState(null);
  const [selectedText, setSelectedText] = useState("");
  const popupRef = useRef(null);
  const [prompt, setPrompt] = useState("Improve");

  const modules = {
    toolbar: { container: `#${id}` },
    markdownOptions: QuillMarkdown,
    history: {
      delay: 1000,
      maxStack: 50,
      userOnly: true,
    },
    clipboard: {
      matchVisual: false,
    },
  };

  const handleSelectionChange = (range, source, editor) => {
    if (readOnly) return;
    setShowAIModal(true);
    if (range && range.length > 0) {
      const text = editor.getText(range.index, range.length);
      let trimedText = text?.trim();
      if (trimedText?.split(" ").length < minWordLength) {
        if (trimedText?.split(" ").length) setSelectionPosition(null);
        return;
      }
      const bounds = editor.getBounds(range.index, range.length);
      const editorMain = editorRef.current.getEditor();
      const editorContainer = editorMain.root.parentElement;
      let finalTextObject = {
        id: idMapping[id],
        text: text,
        range: range,
      };
      const newPosition = {
        top: bounds.top - 50,
        left: bounds.left,
        scrollTop: editorContainer.scrollTop,
      };
      setSelectionPosition(newPosition);

      setSelectedText(() => JSON.stringify(finalTextObject));
      setText(() => JSON.stringify(finalTextObject));
      onTextSelection && onTextSelection({ text, range, bounds });
    } else {
      setSelectedText("");
      // setSelectionPosition(null);
    }
  };

  useEffect(() => {
    if (!selectionPosition) return;

    const editor = editorRef.current.getEditor();
    const editorContainer = editor.root.parentElement;

    const handleScroll = () => {
      setSelectionPosition((prev) => {
        if (!prev) return null;
        return { ...prev, top: prev.top - (editorContainer.scrollTop - prev.scrollTop) };
      });
    };

    editorContainer.addEventListener("scroll", handleScroll);
    return () => editorContainer.removeEventListener("scroll", handleScroll);
  }, [selectionPosition?.top, selectionPosition?.left, selectionPosition?.scrollTop]);

  const replaceHandler = () => {
    if (onReplaceClick) {
      onReplaceClick();
    }
  };

  const regenerateHandler = () => {
    if (onRegenerateClick) {
      onRegenerateClick();
    }
  };

  const handleRephrase = (comingPrompt, text) => {
    setPrompt(comingPrompt);
    rephraseHandler(comingPrompt, text);
  };
  const insertTextHandler = (index, length, text) => {
    if (!index || !length || !text) return;
    const editor = editorRef.current.getEditor();
    editor.deleteText(index, length);
    editor.insertText(index, text);
  };
  useEffect(() => {
    if (generatedText) {
      const response = JSON.parse(generatedText);
      if (response?.populate) insertTextHandler(response.range.index, response.range.length, response.text);
    }
  }, [generatedText]);

  return (
    <div style={{ position: "relative" }}>
      <ReactQuill
        ref={editorRef}
        onFocus={() => (currentToolbar !== id ? onFocus(id) : null)}
        value={value}
        onChange={setValue}
        placeholder={"Flow guide XR..."}
        modules={modules}
        formats={formats}
        onChangeSelection={handleSelectionChange}
        readOnly={readOnly}
      />

      {selectionPosition && selectedText && showAIModal && (
        <div
          ref={popupRef}
          className="Popup-selection"
          style={{
            position: "absolute",
            top: selectionPosition?.top,
            left: selectionPosition?.left,
            zIndex: 1000,
            backgroundColor: "white",
            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
            padding: "8px",
            borderRadius: "4px",
          }}
        >
          <Dropdown>
            <Dropdown.Toggle>
              <ColorStarsIcon /> Sim <span> AI </span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => {
                  handleRephrase("long", selectedText);
                }}
              >
                <LongerIcon /> <span> Make longer </span>
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleRephrase("short", selectedText)}>
                <ShorterIcon /> <span>Make shorter</span>
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleRephrase("improve", selectedText)}>
                <ImproveIcon /> <span>Improve it</span>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      )}

      {showTextModal && (
        <div style={{ zIndex: "10000" }} className="generative-space-parent">
          <div
            onClick={() => {}}
            className="generative-space"
            style={{
              position: "absolute",
              zIndex: 1000,
              top: selectionPosition?.top >= 1400 ? "auto" : selectionPosition?.top,
              left: selectionPosition?.left,
              bottom: selectionPosition?.top >= 1400 ? 0 : "auto",
            }}
          >
            <div className="gen-head">
              <span className="title">
                <ColorStarsIcon />
                {["short", "long"].includes(prompt) ? `${prompt}er` : "improve"}
              </span>
              <span className="cancel-btn" onClick={() => setShowTextModal(false)}>
                <CircleCrossIcon />
              </span>
            </div>
            <div className="gen-text">
              {modalLoading ? (
                <Skeleton width="90%" height="1rem" count={5} />
              ) : (
                <p className="generated-text">{JSON.parse(generatedText || "{}")?.text || ""}</p>
              )}
            </div>
            <div className="actions">
              <div className="d-flex align-items-center justify-content-start gap-3">
                <span onClick={()=>{}}></span>
                {/* <span className="regen" onClick={() => handleRephrase("improve", selectedText)}>
                  <RotateArrowIcon /> Regenerate
                </span> */}
                <Dropdown>
                  <Dropdown.Toggle>
                    {/* more */}
                    <MoreIcon /> More
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => handleRephrase("long", selectedText)}>
                      <LongerIcon /> <span> Make longer </span>
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleRephrase("short", selectedText)}>
                      <ShorterIcon /> <span>Make shorter</span>
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleRephrase("improve", selectedText)}>
                      <ImproveIcon /> <span>Improve it</span>
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
              <span className="replace-btn" onClick={() => replaceHandler()}>
                Replace
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default QuillEditor;
